import {
  Box,
  buildPath,
  buildTemplatePath,
  Button,
  Card,
  CloseButton,
  DateTimePicker,
  formatPhoneNumber,
  Grid,
  ListContext,
  ListContextValue,
  newToast,
  Page,
  PageRoute,
  pn,
  Stack,
  Typography,
  UpdateButton,
  useElementBreakpoints,
  useUnsavedChanges,
  useValidationErrors,
} from "@compass/core";
import InsertEmoticonIcon from "@mui/icons-material/InsertEmoticon";
import {
  BulkSecondLevelValidation,
  BulkSelectedItemErrors,
  GetCreateTextingConversationsResponseDto,
  LkPersonTypeId,
  PersonDto,
  SaveCreateTextingConversationsRequestDto,
  SaveCreateTextingOptInDto,
  Student,
  StudentGroup,
  TextingOptInDto,
  TextingRecipientDto,
} from "generated/openApi";
import { Chip, IconButton, Popover, ToggleButton, ToggleButtonGroup } from "@mui/material";
import EmojiPicker, { EmojiClickData } from "emoji-picker-react";
import { useRouter } from "hooks";
import { useContext, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import ApiService from "services/apiService";
import { SelectedPersons } from "./SelectedPersons";
import { Input, Select, StudentCustomIdsPaster, SubmitButton } from "components";
import { setAppToast } from "redux/reducers/appSlice";
import produce from "immer";
import { pillToggleGroupStyles } from "pages/HomePage/styles/segmentedControlStyles";

const routeName = "createTextingConversations";

export const createTextingConversationsPageRoute: PageRoute = {
  handle: "Send Text Messages",
  templatePath: buildTemplatePath(routeName),
  getPath: () => buildPath(routeName),
};

export type CreateTextingConversationsPageState = {
  personTypeId: LkPersonTypeId;
  personIds: number[];
  scheduledDate?: Date;
};

type ConversationTabs = 'students' | 'parents';

export const CreateTextingConversationsPage = () => {
  const dispatch = useDispatch();
  const { breakpointsRef, smallFieldBreakpoints, fullFieldBreakpoints } = useElementBreakpoints();
  const listContext = useContext<ListContextValue>(ListContext);
  const { state } = useLocation();
  const [stateParam] = useState<CreateTextingConversationsPageState>(state);
  const [createDto, setCreateDto] = useState<GetCreateTextingConversationsResponseDto>();
  const properties = createDto?.savingRequestProperties ?? [];
  const [activeTab, setActiveTab] = useState<ConversationTabs>(
    stateParam.personTypeId === LkPersonTypeId.Parent ? "parents" : "students"
  );
  const [studentPersons, setStudentPersons] = useState<PersonDto[]>([]);
  const [parentPersons, setParentPersons] = useState<PersonDto[]>([]);
  const persons = activeTab === "parents" ? parentPersons : studentPersons;

  const [saveRequest, setSaveRequest] = useState<SaveCreateTextingConversationsRequestDto>({
    personTypeId: stateParam.personTypeId,
    scheduledDate: stateParam.scheduledDate,
  } as SaveCreateTextingConversationsRequestDto);

  // const [resource, setResource] = useState<CreateTextingConversationsResource>({});
  const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({
    trackedItems: [saveRequest, studentPersons, parentPersons],
  });
  const { validationErrors, setValidationErrors } = useValidationErrors();
  const [selectedItemsErrors, setSelectedItemsErrors] = useState<BulkSelectedItemErrors[]>([]);

  // Add new student
  const [optInStudentNumbers, setOptInStudentNumbers] = useState<TextingOptInDto[]>([]);
  const [optInParentNumbers, setOptInParentNumbers] = useState<TextingOptInDto[]>();

  const [optInNumbers, setOptInNumbers] = useState<TextingOptInDto[]>([]);
  const [selectedOptInNumber, setSelectedOptInNumber] = useState<TextingOptInDto>();
  const [emojiAnchorEl, setEmojiAnchorEl] = useState<HTMLElement | null>(null);

  const canSave = unsavedChanges && persons && persons?.length > 0;
  const emojiPickerOpen = Boolean(emojiAnchorEl);

  const isParentTab = activeTab === "parents";
  const studentServiceTypes = createDto?.studentServiceTypes?.filter((x) => x.isForParent === isParentTab) || [];
  const studentServiceSubTypes =
    createDto?.studentServiceSubTypes?.filter((x) => x.serviceTypeId === saveRequest.studentServiceTypeId) || [];

  useEffect(() => {
    if (!saveRequest.studentServiceTypeId) return;

    const selectedServiceType = createDto?.studentServiceTypes?.find((x) => x.id === saveRequest.studentServiceTypeId);
    if (selectedServiceType && selectedServiceType.isForParent !== isParentTab) {
      setSaveRequest((prev) => ({
        ...prev,
        studentServiceTypeId: undefined,
        studentServiceSubTypeId: undefined,
      }));
    }
  }, [activeTab, createDto?.studentServiceTypes, saveRequest.studentServiceTypeId]);

  const handleViewChange = (_: React.MouseEvent<HTMLElement>, newTab: ConversationTabs | null) => {
    if (!newTab || newTab === activeTab) return;

    setActiveTab(newTab);
    setSelectedOptInNumber(undefined); // Clear any staging input values when switching tabs
    setSaveRequest((prev) => ({
      ...prev,
      personTypeId: newTab === "parents" ? LkPersonTypeId.Parent : LkPersonTypeId.Student,
    }));

    if (newTab === "parents") {
      const selectedStudentIds = new Set(studentPersons.map((student) => student.id));
      const associatedParentIds = Array.from(
        new Set(
          (optInParentNumbers ?? []).reduce<number[]>((parentIds, parent) => {
            if (
              parent.parentId &&
              parent.students?.some((student) => selectedStudentIds.has(student.studentId))
            ) {
              parentIds.push(parent.parentId);
            }

            return parentIds;
          }, [])
        )
      );

      setParentPersons([]);
      if (associatedParentIds.length === 0) return;

      ApiService.textingConversations
        .getCreateConversations({
          personTypeId: LkPersonTypeId.Parent,
          personIds: associatedParentIds,
        })
        .then((response) => {
          setParentPersons(response.persons?.filter((person) => person.isParent) ?? []);
        });
    }
  };

  const addEmoji = (emojiData: EmojiClickData) => {
    setSaveRequest((prev) => ({
      ...prev,
      messageBody: `${prev.messageBody || ""}${emojiData.emoji}`,
    }));
  };

  const addMessageVariable = (variable: string) => {
    setSaveRequest((prev) => ({
      ...prev,
      messageBody: `${prev.messageBody || ""}${variable}`,
    }));
  };

  // fetch initial
  useEffect(() => {
    ApiService.textingConversations
      .getCreateConversations({
        personTypeId: stateParam.personTypeId,
        personIds: stateParam.personIds,
      })
      .then((response) => {
        setCreateDto(response);
        setStudentPersons(response.persons?.filter((person) => !person.isParent) ?? []);
        setParentPersons(response.persons?.filter((person) => person.isParent) ?? []);
      });

    ApiService.textingConversations
      .getOptedInStudentNumbers()
      .then((response) => {
        setOptInStudentNumbers(response);
      });

    ApiService.textingConversations
      .getOptedInParentNumbers()
      .then((response) => {
        setOptInParentNumbers(response);
      });
  }, []);

const save = (request: SaveCreateTextingConversationsRequestDto) => {
  setValidationErrors({});
  setSelectedItemsErrors([]);

  // Map your state persons to match the new C# DTO structure
  const recipients: TextingRecipientDto[] = persons?.map((p) => ({
    id: p.id,
    phoneNumber: p.cellPhone,
    isParent: !!p.isParent
  })) ?? [];

  const savingPayload: SaveCreateTextingConversationsRequestDto = {
    ...request,
    recipients: recipients, // Swapped personIds for recipients
  };

  ApiService.textingConversations
    .saveCreateConversations(savingPayload)
    .then((response) => {
      const bulkSecondLevelValidation: BulkSecondLevelValidation | undefined =
        response as unknown as BulkSecondLevelValidation;

      if (bulkSecondLevelValidation && bulkSecondLevelValidation.badRequest) {
        setSelectedItemsErrors(bulkSecondLevelValidation.selectedItemsErrors || []);
        dispatch(setAppToast(newToast("error")));
      } else {
        resetUnsavedChanges([saveRequest, studentPersons, parentPersons]);
        listContext.loadList?.();
        listContext.clearSelectedIds?.();
        dispatch(setAppToast(newToast("success")));
      }
    })
    .catch((ex) => {
      dispatch(setAppToast(newToast("error")));
      setValidationErrors(ex.data);
    });
};

  const sendOptIns = (request: SaveCreateTextingOptInDto) => {
    setValidationErrors({});
    setSelectedItemsErrors([]);

    var savingPayload: SaveCreateTextingOptInDto = {
      ...request,
      personIds: persons?.filter(p => p.cellPhone && !optInStudentNumbers.some(n => n.phoneNumber == p.cellPhone)).map((x) => x.id) ?? [],
    };

    ApiService.textingConversations
      .saveCreateConversationsOptIn(savingPayload)
      .then((response) => {
        const bulkSecondLevelValidation: BulkSecondLevelValidation | undefined =
          response as unknown as BulkSecondLevelValidation;

        if (bulkSecondLevelValidation && bulkSecondLevelValidation.badRequest) {
          setSelectedItemsErrors(bulkSecondLevelValidation.selectedItemsErrors || []);
          dispatch(setAppToast(newToast("error")));
        } else {
          resetUnsavedChanges([saveRequest, studentPersons, parentPersons]);
          listContext.loadList?.();
          listContext.clearSelectedIds?.();
          dispatch(setAppToast(newToast("success")));
        }
      })
      .catch((ex) => {
        dispatch(setAppToast(newToast("error")));
        setValidationErrors(ex.data);
      });
  };

  const header = (
    <Stack direction="row" justifyContent="space-between">
      <Typography variant="inherit">{createTextingConversationsPageRoute.handle}</Typography>

      <Stack direction="row" alignItems="center" spacing="sm">
        <CloseButton confirm={unsavedChanges} />

        {/* {persons.some(p => p.cellPhone && !optInNumbers.some(n => n.phoneNumber == p.cellPhone)) && (<UpdateButton label="Send Opt-Ins" onClick={() => sendOptIns(saveRequest)} />)} */}

        <SubmitButton
          label={`${saveRequest.scheduledDate ? "Schedule" : "Send"}: ${persons?.length}`}
          onClick={() => save(saveRequest)}
          disabled={!canSave}
        />
      </Stack>
    </Stack>
  );

  function messageContainer() {
    const propertyName = pn<SaveCreateTextingConversationsRequestDto>("messageBody");
    const property = properties.find((x) => x.propertyName === propertyName);
    const previewPerson = persons[0];
    const firstName = previewPerson?.firstName ?? "";
    const lastName = previewPerson?.lastName ?? "";
    const fullName = `${firstName} ${lastName}`.trim();
    const previewMessage = previewPerson
      ? (saveRequest.messageBody ?? "")
          .replace(/#FNAME#/g, firstName)
          .replace(/#LNAME#/g, lastName)
          .replace(/#FULLNAME#/g, fullName)
          .replace(/#GRADE#/g, previewPerson.gradeLevel ?? "")
      : saveRequest.messageBody ?? "";

    return (
      <Stack spacing="xs">
        <Input
          inputProps={{ tabIndex: 2 }}
          modelProperty={property}
          errors={validationErrors[propertyName]}
          multiline
          placeholder="Message"
          value={saveRequest.messageBody}
          onChange={(e) => setSaveRequest({ ...saveRequest, messageBody: e.target.value })}
        />

        <Stack direction="row" justifyContent="flex-start" alignItems="center" spacing="xs">
          <IconButton
            aria-label="Add emoji"
            color="primary"
            onClick={(e) => setEmojiAnchorEl(e.currentTarget)}
            size="small"
          >
            <InsertEmoticonIcon />
          </IconButton>

          <Chip
            clickable
            label="First Name"
            onClick={() => addMessageVariable("#FNAME#")}
            size="small"
            sx={{ backgroundColor: "grey.200", "&:hover": { backgroundColor: "grey.300" } }}
          />

          <Chip
            clickable
            label="Last Name"
            onClick={() => addMessageVariable("#LNAME#")}
            size="small"
            sx={{ backgroundColor: "grey.200", "&:hover": { backgroundColor: "grey.300" } }}
          />

          <Chip
            clickable
            label="Full Name"
            onClick={() => addMessageVariable("#FULLNAME#")}
            size="small"
            sx={{ backgroundColor: "grey.200", "&:hover": { backgroundColor: "grey.300" } }}
          />

          <Chip
            clickable
            label="Grade"
            onClick={() => addMessageVariable("#GRADE#")}
            size="small"
            sx={{ backgroundColor: "grey.200", "&:hover": { backgroundColor: "grey.300" } }}
          />
        </Stack>

        <Popover
          open={emojiPickerOpen}
          anchorEl={emojiAnchorEl}
          onClose={() => setEmojiAnchorEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <EmojiPicker onEmojiClick={addEmoji} height={400} width={350} />
        </Popover>

        <Input
          label="Text Preview"
          multiline
          hideCount
          InputProps={{ readOnly: true }}
          value={previewMessage}
        />
      </Stack>
    );
  }

  function nameField() {
    const propertyName = pn<SaveCreateTextingConversationsRequestDto>("name");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Input
        autoFocus
        inputProps={{ tabIndex: 1 }}
        modelProperty={property}
        errors={validationErrors[propertyName]}
        label="Campaign Name"
        value={saveRequest.name}
        onChange={(e) => setSaveRequest({ ...saveRequest, name: e.target.value })}
      />
    );
  }

  function studentServiceTypeField() {
    const propertyName = pn<SaveCreateTextingConversationsRequestDto>("studentServiceTypeId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        modelProperty={property}
        inputProps={{ tabIndex: 2 }}
        errors={validationErrors[propertyName]}
        value={studentServiceTypes.find((x) => x.id === saveRequest.studentServiceTypeId)}
        options={studentServiceTypes}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          setSaveRequest({
            ...saveRequest,
            studentServiceTypeId: value?.id ?? undefined,
            studentServiceSubTypeId: undefined,
          })
        }
      />
    );
  }

  function studentServiceSubTypeField() {
    const propertyName = pn<SaveCreateTextingConversationsRequestDto>("studentServiceSubTypeId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        modelProperty={property}
        inputProps={{ tabIndex: 3 }}
        errors={validationErrors[propertyName]}
        value={studentServiceSubTypes?.find((x) => x.id === saveRequest.studentServiceSubTypeId)}
        options={studentServiceSubTypes}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          setSaveRequest({
            ...saveRequest,
            studentServiceSubTypeId: value?.id ?? undefined,
          })
        }
      />
    );
  }

  function scheduledDateField() {
    const propertyName = pn<SaveCreateTextingConversationsRequestDto>("scheduledDate");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <DateTimePicker
        label={property?.displayName || "Schedule Time"}
        errors={validationErrors[propertyName]}
        value={saveRequest.scheduledDate ?? null}
        onChange={(value) =>
          setSaveRequest({
            ...saveRequest,
            scheduledDate: value ?? undefined,
          })
        }
      />
    );
  }

  function addPersonField() {
    return (
      <Select
        // modelProperty={property}
        // errors={props.validationErrors[propertyName]}
        label={"Add by Student Name"}
        inputProps={{ tabIndex: 4 }}
        value={optInStudentNumbers?.find((x) => x.studentId === selectedOptInNumber?.studentId)}
        options={optInStudentNumbers}
        getOptionLabel={(option) =>
          `${option.studentLastName}, ${option.studentFirstName} (${formatPhoneNumber(option.phoneNumber)})`
        }
        renderOption={(props, option) => (
          <Box component="li" {...props}>
            <Stack>
              <Stack direction="row">
                <Typography>{option.studentLastName}, {option.studentFirstName}</Typography>
              </Stack>

              <Typography variant="caption">{formatPhoneNumber(option.phoneNumber)}</Typography>
            </Stack>
          </Box>
        )}
        onChange={(e, value) => {
          setSelectedOptInNumber(value)
        }
        }
      />
    );
  }

  function addParentField() {
    return (
      <Select
        inputProps={{ tabIndex: 4 }}
        label={"Add by Parent Name"}
        value={optInParentNumbers?.find((x) => x.parentId === selectedOptInNumber?.parentId)}
        options={optInParentNumbers}
        getOptionLabel={(option) =>
          `${option.parentLastName}, ${option.parentFirstName} (${formatPhoneNumber(option.phoneNumber)})`
        }
        renderOption={(props, option) => (
          <Box component="li" {...props}>
            <Stack spacing="sm">
              {/* Parent Name */}
              <Stack direction="row">
                <Typography sx={{ fontWeight: 'medium' }}>
                  {option.parentLastName}, {option.parentFirstName}
                </Typography>
              </Stack>

              {/* Students List */}
              <Stack direction="row">
                {option.students && option.students.length > 0 && (
                  <Typography variant="body2" color="text.secondary">
                    Students:{' '}
                    {option.students
                      .map((student) => student.studentInfo)
                      .join(', ')}
                  </Typography>
                )}
              </Stack>

              {/* Phone Number */}
              <Typography variant="caption" color="text.secondary">
                {formatPhoneNumber(option.phoneNumber)}
              </Typography>
            </Stack>
          </Box>
        )}
        onChange={(e, value) => setSelectedOptInNumber(value)}
      />
    );
  }

  function staffServiceTypeField() {
    const propertyName = pn<SaveCreateTextingConversationsRequestDto>("staffServiceTypeId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return (
      <Select
        modelProperty={property}
        errors={validationErrors[propertyName]}
        value={createDto?.staffServiceTypes?.find((x) => x.id === saveRequest.staffServiceTypeId)}
        options={createDto?.staffServiceTypes}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          setSaveRequest({
            ...saveRequest,
            staffServiceTypeId: value?.id ?? undefined,
          })
        }
      />
    );
  }

  const addStudentRecords = (students: Student[]) => {
    const studentIds = students.map((student) => student.id);
    if (studentIds.length === 0) return;

    ApiService.textingConversations
      .getStudentPersons(studentIds)
      .then((persons) => {
        setStudentPersons((prev) => {
          const updatedPersons = new Map(
            prev.map((person) => [`student-${person.id}`, person])
          );

          persons.forEach((person) => {
            updatedPersons.set(`student-${person.id}`, person);
          });

          return Array.from(updatedPersons.values());
        });
      });
  };

  const addStudentsByCustomIds = (customIdTypeId: number | undefined, customIds: string[]) => {
    if (!customIdTypeId || customIds.length === 0) return;

    ApiService.students
      .getByCustomIds({ customIdTypeId, customIds })
      .then((students) => addStudentRecords(students));
  };

  const addStudentsByGroup = (groupId: number) => {
    ApiService.students
      .getGroupStudents(groupId)
      .then((students) => addStudentRecords(students));
  };

const addSelectedStudent = () => {
    if (!selectedOptInNumber?.studentId) return;

    ApiService.textingConversations
      .getStudentPersons([selectedOptInNumber.studentId])
      .then((persons) => {
        setStudentPersons((prev) => {
          const updatedPersons = new Map(prev.map((person) => [`student-${person.id}`, person]));
          persons.forEach((person) => updatedPersons.set(`student-${person.id}`, person));
          return Array.from(updatedPersons.values());
        });
        setSelectedOptInNumber(undefined);
      });
  };

const addSelectedParent = () => {
    if (!selectedOptInNumber?.parentId) return;

    ApiService.textingConversations
      .getCreateConversations({
        personTypeId: LkPersonTypeId.Parent,
        personIds: [selectedOptInNumber.parentId],
      })
      .then((response) => {
        setParentPersons((prev) => {
          const updatedPersons = new Map(prev.map((person) => [`parent-${person.id}`, person]));
          response.persons?.forEach((person) => updatedPersons.set(`parent-${person.id}`, person));
          return Array.from(updatedPersons.values());
        });
        setSelectedOptInNumber(undefined);
      });
  };

  const addStudentHeader = (
    <Stack direction="row" justifyContent={"space-between"} alignItems={"center"} spacing="sm">
      <Stack direction="row" spacing="md" alignItems="center">
        <Stack>Add Student</Stack>
      </Stack>

      <Stack direction="row" spacing="sm" alignItems="center">
        {selectedOptInNumber?.customId1 && (
          <Button onClick={() => addSelectedStudent()} variant="outlined" color="info">
            + Add
          </Button>
        )}
      </Stack>
    </Stack>
  );

  const addStudentParentHeader = (
    <Stack direction="row" justifyContent={"space-between"} alignItems={"center"} spacing="sm">
      <Stack direction="row" spacing="md" alignItems="center">
        <Stack>Add Parent</Stack>
      </Stack>

      <Stack direction="row" spacing="sm" alignItems="center">
        {selectedOptInNumber?.parentId && (
          <Button onClick={() => addSelectedParent()} variant="outlined" color="info">
            + Add
          </Button>
        )}
      </Stack>
    </Stack>
  );

  return (
    <>
      <Page isForm header={header}>
        <Stack spacing="lg" ref={breakpointsRef}>
          <Card header="Send Text to:">

            <Box>
              <Grid container spacing="sm">
                <Grid item xs={smallFieldBreakpoints}>
                  <ToggleButtonGroup
                    value={activeTab}
                    exclusive
                    onChange={handleViewChange}
                    sx={pillToggleGroupStyles}
                  >
                    <ToggleButton value="students">Students</ToggleButton>
                    <ToggleButton value="parents" disabled={optInParentNumbers === undefined}>
                      Parents
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Grid>
              </Grid>
            </Box>
          </Card>

          <Card header="Compose Text Message">
            <Stack direction="column" spacing="md">
              {nameField()}
              {messageContainer()}

              <Box>
                <Grid container spacing="sm">
                  <Grid item xs={smallFieldBreakpoints}>
                    {studentServiceTypeField()}
                  </Grid>

                  {studentServiceSubTypes.length > 0 && (
                    <Grid item xs={smallFieldBreakpoints}>
                      {studentServiceSubTypeField()}
                    </Grid>
                  )}

                  <Grid item xs={smallFieldBreakpoints}>
                    {scheduledDateField()}
                  </Grid>

                  {/* <Grid item xs={smallFieldBreakpoints}>
                  {staffServiceTypeField()}
                </Grid> */}
                </Grid>
              </Box>
            </Stack>
          </Card>

          {optInStudentNumbers && activeTab == "students" && (
            <Card header={addStudentHeader}>
              <Stack direction="column" spacing="md">
                <Grid container spacing="sm">
                  <Grid item xs={smallFieldBreakpoints}>
                    {addPersonField()}
                  </Grid>

                  <Grid item xs={smallFieldBreakpoints}>
                    <Select
                      label="Add by Group"
                      value={undefined as unknown as StudentGroup}
                      onChange={(e, value) => addStudentsByGroup(value.id)}
                      options={createDto?.studentGroups}
                      renderOption={(props, option) => (
                        <Box component="li" {...props}>
                          {option.name}
                        </Box>
                      )}
                      getOptionLabel={() => ""}
                    />
                  </Grid>

                  <Grid item xs={fullFieldBreakpoints}>
                    <StudentCustomIdsPaster onAdd={addStudentsByCustomIds} />
                  </Grid>
                </Grid>
              </Stack>
            </Card>
          )}

          {optInParentNumbers && activeTab == "parents" && (
            <Card header={addStudentParentHeader}>
              <Stack direction="column" spacing="md">
                <Grid container spacing="sm">
                  <Grid item xs={5}>
                    {addParentField()}
                  </Grid>
                </Grid>
              </Stack>
            </Card>
          )}

          {(optInStudentNumbers != null && optInParentNumbers != null) && (<SelectedPersons
            optInNumbers={[...optInStudentNumbers, ...optInParentNumbers]}
            persons={new Map(persons?.map((x) => [`${x.isParent ? 'parent' : 'student'}-${x.id}`, x]))}
            setPersons={(value) => {
              const updatedPersons = Array.from(value.values());

              if (activeTab === "parents") {
                setParentPersons(updatedPersons);
              } else {
                setStudentPersons(updatedPersons);
              }
            }}
            errors={selectedItemsErrors}
            setErrors={setSelectedItemsErrors}
          />
          )}
        </Stack>
      </Page>
    </>
  );
};
